//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import {
    InputRenderStateHandlers,
    inputWithRendererFactory
} from 'common/components/core/input/InputWithRenderer';
import { EnhancedInput } from 'common/components/core/input';
import defaultTheme from './EditableCell.scss';
import DataUtils from 'common/utils/DataUtils';
import Icon, { IconWithPopover } from 'common/components/core/icon';
import { dashboardColors } from 'common/enums/ColorEnum';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { Loader } from 'common/components/core/loader/Loader';
import { SizeEnum } from 'common/enums/UIEnums';
import * as Immutable from 'immutable';

const InputRenderer = ({
    theme,
    inputValue,
    isFocused,
    placeholder,
    error,
    leftIcon,
    leftIconPopover,
    leftIconColor,
    isInputLoading
}) => {
    return !isFocused ? (
        <div
            className={classnames(theme.inputRenderer, {
                [theme.leftIconWrap]: leftIcon,
                [theme.hasError]: error
            })}
        >
            {leftIcon ? (
                <IconWithPopover
                    className={theme.leftIcon}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    theme={theme}
                    icon={leftIcon}
                    iconColor={leftIconColor}
                    popOverContent={leftIconPopover}
                />
            ) : null}
            <div className={theme.text}>
                {!DataUtils.isEmpty(inputValue) ? inputValue : placeholder}
            </div>
            <div className={theme.right}>
                {error && !isInputLoading ? (
                    <Icon
                        theme={theme}
                        invert
                        bgColor={dashboardColors.salmonTwo.name}
                        icon="alert"
                        className={theme.errorIcon}
                    />
                ) : null}
                {isInputLoading ? (
                    <Loader
                        theme={theme}
                        active={isInputLoading}
                        size={SizeEnum.Small}
                    />
                ) : null}
            </div>
        </div>
    ) : null;
};

const EnhancedInputWithRenderer = InputRenderStateHandlers(
    inputWithRendererFactory({
        InputRenderer,
        InputComponent: EnhancedInput
    })
);

type OnChangeMetaTypes = {
    rowId: string,
    headerId: string,
    row: Immutable.Map<*, *>,
    header: Immutable.Map<*, *>,
    rowIndex: number,
    pageNumber: number,
    paginatedRowIndex: number
};

type Props = {
    theme: Object,
    inputValue?: string,
    cellId?: string,
    error?: string,
    leftIcon?: string,
    leftIconPopover?: ?string,
    leftIconColor?: ?string,
    disabled?: boolean,
    readOnly?: boolean,
    onChange?: (value: string) => void,
    onCellEdit?: (value: string, onChangeMetaData: OnChangeMetaTypes) => void,
    onFocus?: Function,
    isActive: boolean,
    isErrorLoading: boolean,
    greyedOutBG: boolean,
    setRef?: Function,
    onBlur: Function,
    onChangeMetaData: OnChangeMetaTypes
};
type State = {
    value: string
};

class EditableCell extends React.PureComponent<Props, State> {
    state = {
        value: ''
    };

    componentDidUpdate({ isActive: prevIsActive }) {
        const { isActive, setRef } = this.props;
        if (setRef && prevIsActive !== isActive) {
            setRef(this.inputRef); // when input is already visible
        }
    }

    inputRef: React.ElementRef<any> | null = null;

    handleChange = (value: string, ...args) => {
        const { onChange } = this.props;
        this.setState({ value });
        onChange && onChange(value, ...args);
    };

    handleFocus = (...args) => {
        const { inputValue, onFocus } = this.props;
        this.setState({ value: inputValue });
        onFocus && onFocus(...args);
    };

    handleBlur = (...args) => {
        const { onCellEdit, inputValue, onBlur, onChangeMetaData } = this.props;

        this.setState(prevState => {
            if (prevState.value !== inputValue) {
                onCellEdit &&
                    onCellEdit(prevState.value, onChangeMetaData, ...args);
                return { value: '' };
            }
        });
        if (onBlur) {
            onBlur(...args);
        }
    };

    setInputRef = (ref: React.ElementRef<any>): void => {
        const { setRef } = this.props;
        this.inputRef = ref;
        if (setRef) {
            setRef(ref);
        }
    };

    render() {
        const {
            inputValue,
            theme,
            error,
            leftIcon,
            leftIconPopover,
            leftIconColor,
            disabled,
            readOnly,
            isErrorLoading,
            greyedOutBG
        } = this.props;
        const { value } = this.state;
        return (
            <EnhancedInputWithRenderer
                wrapperClassName={greyedOutBG && theme.darkBG}
                disabled={disabled}
                readOnly={readOnly}
                inputValue={inputValue}
                value={value}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                theme={theme}
                error={error}
                defaultFocused={false}
                preferPlace={PopoverPlaceEnum.Right}
                leftIcon={leftIcon}
                leftIconPopover={leftIconPopover}
                leftIconColor={leftIconColor}
                isInputLoading={isErrorLoading}
                setRef={this.setInputRef}
            />
        );
    }
}
const ThemedEditableCell = themr('EditableCell', defaultTheme)(EditableCell);
export { ThemedEditableCell as EditableCell };
export default ThemedEditableCell;
